import { FormEvent, useEffect, useState } from "react";
import { FiCopy } from "react-icons/fi";
import { toast } from "react-toastify";

import Modal from "../../components/Reusable/Modal";
import {
  REFERRAL_CODE_STATUS_OPTIONS,
  ReferralCode,
  ReferralCodePayload,
  ReferralCodeStatus,
  ReferralUserOption,
} from "./types";

const generateReferralCode = () => {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const values = new Uint32Array(8);

  if (window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(values);
  } else {
    values.forEach((_, index) => {
      values[index] = Math.floor(Math.random() * alphabet.length);
    });
  }

  return `DG${Array.from(values, (value) => alphabet[value % alphabet.length]).join("")}`;
};

const userLabel = (user: ReferralUserOption) => {
  const name = [user.firstname, user.lastname].filter(Boolean).join(" ");
  return `${name || user.username || "Unnamed user"}${
    user.email ? ` — ${user.email}` : ""
  }`;
};

const referralStatusDetails = (status: ReferralCodeStatus) =>
  REFERRAL_CODE_STATUS_OPTIONS.find((option) => option.value === status) ??
  REFERRAL_CODE_STATUS_OPTIONS[0];

type ReferralCodeModalProps = {
  isOpen: boolean;
  users: ReferralUserOption[];
  usersLoading: boolean;
  userSearch: string;
  isSubmitting: boolean;
  onUserSearchChange: (value: string) => void;
  onClose: () => void;
  onSubmit: (payload: ReferralCodePayload) => Promise<void>;
};

export const ReferralCodeModal = ({
  isOpen,
  users,
  usersLoading,
  userSearch,
  isSubmitting,
  onUserSearchChange,
  onClose,
  onSubmit,
}: ReferralCodeModalProps) => {
  const [code, setCode] = useState("");
  const [userId, setUserId] = useState("");
  const [status, setStatus] = useState<ReferralCodeStatus>("active");

  useEffect(() => {
    if (!isOpen) return;

    setCode(generateReferralCode());
    setUserId("");
    setStatus("active");
  }, [isOpen]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const normalizedCode = code.trim().toUpperCase();

    if (!normalizedCode || !userId) {
      toast.error("Enter a referral code and select a user.");
      return;
    }

    await onSubmit({
      code: normalizedCode,
      user_id: userId,
      status,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-xl">
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Create referral code
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Assign one permanent code to a user and choose whether it should
            accept signups immediately.
          </p>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="referral-user-search"
            className="block text-sm font-medium text-gray-700 dark:text-gray-200"
          >
            Find available user
          </label>
          <input
            id="referral-user-search"
            type="search"
            value={userSearch}
            onChange={(event) => onUserSearchChange(event.target.value)}
            placeholder="Search by name, email, or user ID"
            className="h-12 w-full rounded-lg border border-gray-300 bg-white px-3 text-gray-900 outline-none focus:border-pry-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
          />
        </div>

        <div className="space-y-2">
          <label
            htmlFor="referral-user-id"
            className="block text-sm font-medium text-gray-700 dark:text-gray-200"
          >
            Assigned user
          </label>
          <select
            id="referral-user-id"
            value={userId}
            onChange={(event) => setUserId(event.target.value)}
            required
            disabled={usersLoading && users.length === 0}
            className="h-12 w-full rounded-lg border border-gray-300 bg-white px-3 text-gray-900 outline-none focus:border-pry-500 disabled:opacity-60 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
          >
            <option value="">
              {usersLoading ? "Loading users…" : "Select a user"}
            </option>
            {users.map((user) => (
              <option key={String(user.user_id)} value={String(user.user_id)}>
                {userLabel(user)}
              </option>
            ))}
          </select>
          {!usersLoading && users.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              No matching users found. Try a different search.
            </p>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Each user can own only one referral code.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label
            htmlFor="referral-code"
            className="block text-sm font-medium text-gray-700 dark:text-gray-200"
          >
            Referral code
          </label>
          <div className="flex gap-2">
            <input
              id="referral-code"
              value={code}
              onChange={(event) =>
                setCode(
                  event.target.value
                    .toUpperCase()
                    .replace(/[^A-Z0-9_-]/g, ""),
                )
              }
              required
              minLength={3}
              maxLength={32}
              pattern="[A-Z0-9_-]{3,32}"
              title="Use 3–32 letters, numbers, underscores, or hyphens."
              autoComplete="off"
              className="h-12 min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 font-mono uppercase tracking-wide text-gray-900 outline-none focus:border-pry-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
            />
            <button
              type="button"
              onClick={() => setCode(generateReferralCode())}
              className="rounded-lg border border-gray-300 px-4 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-zinc-700 dark:text-gray-200 dark:hover:bg-zinc-800"
            >
              Generate
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Codes are saved in uppercase, must be unique, and cannot be changed
            after creation.
          </p>
        </div>

        <div className="space-y-2">
          <label
            htmlFor="referral-status"
            className="block text-sm font-medium text-gray-700 dark:text-gray-200"
          >
            Initial status
          </label>
          <select
            id="referral-status"
            value={status}
            onChange={(event) =>
              setStatus(event.target.value as ReferralCodeStatus)
            }
            className="h-12 w-full rounded-lg border border-gray-300 bg-white px-3 text-gray-900 outline-none focus:border-pry-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
          >
            {REFERRAL_CODE_STATUS_OPTIONS.filter(
              (option) => option.value !== "deleted",
            ).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {referralStatusDetails(status).description}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Deleted is a permanent state available after the code is created.
          </p>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 pt-5 dark:border-zinc-800">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 disabled:opacity-60 dark:border-zinc-700 dark:text-gray-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="rounded-lg bg-gradient-to-r from-pry-500 to-pry-600 px-5 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? "Saving…" : "Create code"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

type ReferralCodesTableProps = {
  referralCodes: ReferralCode[];
  loading: boolean;
  updatingId: number | null;
  onStatusChange: (
    referralCode: ReferralCode,
    status: ReferralCodeStatus,
  ) => void;
};

export const ReferralCodesTable = ({
  referralCodes,
  loading,
  updatingId,
  onStatusChange,
}: ReferralCodesTableProps) => {
  const copyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Referral code copied.");
    } catch {
      toast.error("Unable to copy the referral code.");
    }
  };

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-zinc-800">
      <table className="w-full min-w-[760px] text-left text-sm">
        <thead className="bg-gray-50 text-xs uppercase text-gray-500 dark:bg-zinc-900 dark:text-gray-400">
          <tr>
            <th className="px-5 py-4">Assigned user</th>
            <th className="px-5 py-4">Referral code</th>
            <th className="px-5 py-4">Attributed signups</th>
            <th className="px-5 py-4">Status</th>
            <th className="px-5 py-4">Created</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-zinc-800">
          {loading ? (
            <tr>
              <td colSpan={5} className="px-5 py-14 text-center">
                <span className="inline-block h-9 w-9 animate-spin rounded-full border-2 border-pry-500 border-t-transparent" />
              </td>
            </tr>
          ) : referralCodes.length === 0 ? (
            <tr>
              <td
                colSpan={5}
                className="px-5 py-14 text-center text-gray-500 dark:text-gray-400"
              >
                No referral codes match these filters.
              </td>
            </tr>
          ) : (
            referralCodes.map((referralCode) => {
              const user = referralCode.user;
              const fullName = [user?.firstname, user?.lastname]
                .filter(Boolean)
                .join(" ");
              const isUpdating = updatingId === referralCode.id;
              const statusDetails = referralStatusDetails(referralCode.status);

              return (
                <tr
                  key={referralCode.id}
                  className="bg-white text-gray-700 dark:bg-zinc-950 dark:text-gray-200"
                >
                  <td className="px-5 py-4">
                    <div className="font-semibold">
                      {fullName || "Unnamed user"}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {user?.email || `User ID: ${referralCode.user_id}`}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    {referralCode.status === "active" ? (
                      <button
                        type="button"
                        onClick={() => copyCode(referralCode.code)}
                        className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 font-mono font-semibold tracking-wide hover:bg-gray-200 dark:bg-zinc-800 dark:hover:bg-zinc-700"
                        title="Copy active referral code"
                      >
                        {referralCode.code}
                        <FiCopy aria-hidden="true" />
                      </button>
                    ) : (
                      <span
                        className="inline-flex rounded-lg bg-gray-100 px-3 py-2 font-mono font-semibold tracking-wide text-gray-500 dark:bg-zinc-800 dark:text-gray-400"
                        title="This code is not accepting new signups"
                      >
                        {referralCode.code}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    {referralCode.referred_users_count ?? 0}
                  </td>
                  <td className="px-5 py-4">
                    <div className="min-w-[260px] space-y-1.5">
                      {referralCode.status === "deleted" ? (
                        <span className="inline-flex h-10 items-center rounded-full bg-red-100 px-4 font-semibold text-red-700 dark:bg-red-950/50 dark:text-red-300">
                          Deleted
                        </span>
                      ) : (
                        <>
                          <label
                            className="sr-only"
                            htmlFor={`status-${referralCode.id}`}
                          >
                            Status for {referralCode.code}
                          </label>
                          <select
                            id={`status-${referralCode.id}`}
                            value={referralCode.status}
                            disabled={isUpdating}
                            onChange={(event) =>
                              onStatusChange(
                                referralCode,
                                event.target.value as ReferralCodeStatus,
                              )
                            }
                            className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3 font-semibold text-gray-700 outline-none focus:border-pry-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-700 dark:bg-zinc-900 dark:text-gray-200"
                          >
                            {REFERRAL_CODE_STATUS_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </>
                      )}
                      <p className="text-xs leading-5 text-gray-500 dark:text-gray-400">
                        {isUpdating
                          ? "Updating status…"
                          : statusDetails.description}
                      </p>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    {formatDate(referralCode.created_at)}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};
